import {
  Container,
  Heading,
  Stack,
  Text,
  SimpleGrid,
  Box,
  Icon,
  Button,
  keyframes,
} from "@chakra-ui/react";
import { useReducedMotion } from "framer-motion";
import NextLink from "next/link";
import type { ReactElement } from "react";
import { BsFillCloudLightningRainFill } from "react-icons/bs";
import { FiArrowRight } from "react-icons/fi";
import { ImFire } from "react-icons/im";
import { IoWaterSharp, IoWarningSharp } from "react-icons/io5";

interface FeatureProps {
  title: string;
  text: string;
  icon: ReactElement;
  delay?: number;
}

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Feature = ({ title, text, icon, delay = 0 }: FeatureProps) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <Box
      bg="neutral.800"
      p={8}
      borderRadius="lg"
      border="1px solid"
      borderColor="neutral.800"
      transition="border-color 0.2s ease"
      animation={
        shouldReduceMotion
          ? undefined
          : `${fadeInUp} 0.5s ease-out ${delay}s both`
      }
      _hover={{ borderColor: "rgba(255, 79, 0, 0.3)" }}
    >
      <Stack spacing={4} textAlign="left">
        <Box
          w={12}
          h={12}
          bg="rgba(255, 79, 0, 0.12)"
          borderRadius="md"
          display="flex"
          alignItems="center"
          justifyContent="center"
          color="brand.500"
        >
          {icon}
        </Box>

        <Heading fontSize="lg" fontWeight={700} color="text.dark-primary">
          {title}
        </Heading>

        <Text color="text.dark-muted" lineHeight="1.7" fontSize="md">
          {text}
        </Text>
      </Stack>
    </Box>
  );
};

export default function Feature1() {
  return (
    <Box bg="neutral.900" py={{ base: 20, md: 28 }}>
      <Container maxW="6xl">
        <Stack align="center" spacing={{ base: 10, md: 14 }}>
          {/* Section header */}
          <Stack spacing={5} maxW="2xl" textAlign="center">
            <Heading
              color="white"
              fontWeight={800}
              fontSize={{ base: "3xl", sm: "4xl", md: "5xl" }}
              lineHeight="1.2"
              letterSpacing="-0.02em"
            >
              Simple, Reliable &{" "}
              <Text as="span" color="brand.500">
                Always Watching
              </Text>
            </Heading>
            <Text
              color="text.on-black-secondary"
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
            >
              Once your devices are registered, alerts are on automatically — no
              extra setup needed. We notify you the moment conditions change
              near your location.
            </Text>
          </Stack>

          {/* Feature cards */}
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={{ base: 5, md: 6 }}
            w="full"
          >
            <Feature
              icon={<Icon as={BsFillCloudLightningRainFill} w={6} h={6} />}
              title="Severe Weather"
              text="Severe thunderstorms, tornados, extreme heat or cold, and dangerous marine conditions can develop quickly. Stay informed and prepared."
              delay={0}
            />
            <Feature
              icon={<Icon as={ImFire} w={6} h={6} />}
              title="Wildfires"
              text="Wildfires evolve rapidly as wind direction changes. Know when conditions become dangerous or when you're suddenly in a fire's path."
              delay={0.05}
            />
            <Feature
              icon={<Icon as={IoWaterSharp} w={6} h={6} />}
              title="Flooding & Tsunamis"
              text="Distant storms or earthquakes can lead to flash flooding or tsunamis. Get alerted before it's too late to reach higher ground."
              delay={0.1}
            />
            <Feature
              icon={<Icon as={IoWarningSharp} w={6} h={6} />}
              title="Other Safety Risks"
              text="From natural hazards to critical infrastructure failures, we'll send you alerts you might otherwise never receive."
              delay={0.15}
            />
          </SimpleGrid>

          {/* CTA */}
          <Button
            as={NextLink}
            href="/login"
            bg="brand.500"
            color="white"
            px={8}
            py={6}
            fontSize="md"
            fontWeight={600}
            borderRadius="md"
            rightIcon={<Icon as={FiArrowRight} aria-hidden="true" />}
            _hover={{
              bg: "brand.600",
              transform: "translateY(-1px)",
              textDecoration: "none",
            }}
            transition="transform 0.2s ease, background-color 0.2s ease"
          >
            Get Protected Today
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}
